import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';
import { useMemo } from 'react';

const httpClient = new HttpClient();

interface LocationReadingsParams {
  id: string | number;
  date?: string;
  onlyActive?: boolean;
  locationVisitId?: number;
}

interface LocationDetailsParams {
  id: string | number;
}

interface LocationDetailsResponse {
  location: any;
  address: any;
  atms: any[];
  trks: any[];
  goas: any[];
  ctms: any[];
  resource: any;
  contacts: any[];
  locationContacts: any[];
}

interface LocationReadingsResponse {
  atmEvents: any[];
  trkEvents: any[];
  goaEvents: any[];
  ctmEvents: any[];
  outages: any[];
  atms: any[];
  trks: any[];
  goas: any[];
  ctms: any[];
  locationVisitId?: number;
}

interface GenerateReportParams {
  locationId: number;
  reportId?: number;
  preview?: boolean;
  sendToPrimaryContact?: boolean;
  serviceDate?: string;
  signatureResource?: any;
  identityResource?: any;
}

interface GenerateReportResponse {
  html: string;
  success: boolean;
}

interface QueryReportsParams {
  locationId: number;
  skip: number;
  take: number;
  search: string;
  orderBy: string;
  orderDirection: 'ASC' | 'DESC';
}

interface QueryReportsResponse {
  data: any[];
  total: number;
  take: number;
  resources: any[];
}

const fetchLocationReadings = async (params: LocationReadingsParams): Promise<LocationReadingsResponse> => {
  // Only include date parameter if it's a valid date string
  const queryParams: any = { id: params.id };
  if (params.date && params.date !== 'Invalid Date' && !isNaN(Date.parse(params.date))) {
    queryParams.date = params.date;
  }
  if (params.onlyActive) {
    queryParams.onlyActive = 'true';
  }
  if (params.locationVisitId) {
    queryParams.locationVisitId = params.locationVisitId.toString();
  }
  
  const response = await httpClient.get<LocationReadingsResponse>('api/location/todays-readings', queryParams);
  return response.data;
};

const fetchLocationDetails = async (params: LocationDetailsParams): Promise<LocationDetailsResponse> => {
  const response = await httpClient.get<LocationDetailsResponse>('api/location/details', params);
  return response.data;
};

export const useLocationReadings = (locationId: string | number, date?: string, onlyActive?: boolean, locationVisitId?: number) => {
  // Validate date parameter
  const validDate = date && date !== 'Invalid Date' && !isNaN(Date.parse(date)) ? date : undefined;
  
  return useQuery({
    queryKey: ['location-readings', locationId, validDate, onlyActive, locationVisitId],
    queryFn: () => fetchLocationReadings({ id: locationId, date: validDate, onlyActive, locationVisitId }),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

export const useLocationDetails = (locationId: string | number) => {
  return useQuery({
    queryKey: ['location-details', locationId],
    queryFn: () => fetchLocationDetails({ id: locationId }),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

const fetchGenerateReport = async (params: GenerateReportParams): Promise<GenerateReportResponse> => {
  const response = await httpClient.post<GenerateReportResponse>('api/location/generate-report', params);
  return response.data;
};

const fetchQueryReports = async (params: QueryReportsParams): Promise<QueryReportsResponse> => {
  const response = await httpClient.post<QueryReportsResponse>('api/location/query-reports', params);
  return response.data;
};

const fetchReportsByDate = async (locationId: number, serviceDate: string): Promise<{ reports: any[], count: number }> => {
  const response = await httpClient.get(`api/location/reports-by-date/${locationId}/${serviceDate}`, {});
  return response.data;
};

export const useGenerateReport = () => {
  return useMutation({
    mutationFn: fetchGenerateReport,
    onSuccess: (data) => {
      console.log('Report generated successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to generate report:', error);
    },
  });
};

export const useQueryReports = (locationId: number) => {
  return useQuery({
    queryKey: ['location-reports', locationId],
    queryFn: () => fetchQueryReports({ 
      locationId, 
      skip: 0, 
      take: 100, // Default values, can be overridden
      search: '',
      orderBy: 'id',
      orderDirection: 'DESC'
    }),
    enabled: !!locationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useQueryReportsWithParams = () => {
  return useMutation({
    mutationFn: fetchQueryReports,
    onSuccess: (data) => {
      console.log('Reports queried successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to query reports:', error);
    },
  });
};

export const useReportsByDate = (locationId: number | string | undefined, serviceDate: string | undefined) => {
  return useQuery({
    queryKey: ['reports-by-date', locationId, serviceDate],
    queryFn: () => fetchReportsByDate(Number(locationId!), serviceDate!),
    enabled: !!locationId && !!serviceDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const fetchAllLocations = async () => {
  const response = await httpClient.post('api/location/query', {
    skip: 0,
    take: 1000,
    search: '',
    orderBy: 'id',
    orderDirection: 'ASC',
  });
  return response.data.data || [];
};

export const useCreateLocation = () =>
  useMutation({
    mutationFn: async (location: any) => {
      const response = await httpClient.post('api/location/create', location);
      return response.data;
    },
  });

export const useReorderGoas = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (goaOrderData: { goas: Array<{ id: string | number; position: number }> }) => {
      const response = await httpClient.post('api/goa/reorder', goaOrderData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate location details to refresh the table
      queryClient.invalidateQueries({ queryKey: ['location-details'] });
      console.log('GOAs reordered successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to reorder GOAs:', error);
    },
  });
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (location: any) => {
      const response = await httpClient.post('api/location/update', location);
      return response.data;
    },
    onSuccess: (data, variables) => {
      const locationId = variables.id;
      const locationIdStr = String(locationId);
      const locationIdNum = Number(locationId);
      
      // Update React Query cache for all possible key formats (string, number, etc.)
      [locationId, locationIdStr, locationIdNum].forEach((id) => {
        if (id && data?.location) {
          queryClient.setQueryData(['location-details', id], (oldData: any) => {
            if (!oldData) {
              return {
                location: data.location,
                address: data.address || null,
                atms: [],
                trks: [],
                goas: [],
                ctms: [],
                resource: null,
                locationContacts: [],
                contacts: [],
              };
            }
            return {
              ...oldData,
              location: data.location,
              address: data.address !== undefined ? data.address : oldData.address,
            };
          });
        }
      });
      
      // Invalidate queries to trigger background refetch
      queryClient.invalidateQueries({ queryKey: ['location-details'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
    onError: (error) => {
      console.error('Failed to update location:', error);
    },
  });
};

interface QueryLocationsParams {
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  [key: string]: any;
}

interface QueryLocationsResponse {
  data: any[];
  total: number;
}

const queryLocationsApi = async (params: QueryLocationsParams): Promise<QueryLocationsResponse> => {
  const response = await httpClient.post<QueryLocationsResponse>('api/location/query', params);
  return response.data;
};

export const useQueryLocations = () => {
  return useMutation({
    mutationFn: queryLocationsApi,
  });
};

export const useLocations = () => {
  return useQuery({
    queryKey: ['locations'],
    queryFn: fetchAllLocations,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UpsertGoaEventsParams {
  locationId: number;
  goaEvents: any[];
  serviceDate?: string;
}

interface UpsertTrkEventsParams {
  locationVisitId: number;
  locationId: number;
  trks: any[];
}

interface UpsertAtmEventsParams {
  locationVisitId: number;
  events: any[];
}

const upsertGoaEventsApi = async (params: UpsertGoaEventsParams): Promise<any> => {
  const response = await httpClient.post('api/location/upsert-goa-events', params);
  return response.data;
};

const upsertTrkEventsApi = async (params: UpsertTrkEventsParams): Promise<any> => {
  const response = await httpClient.post('api/location/upsert-trk-events', params);
  return response.data;
};

const upsertAtmEventsApi = async (params: UpsertAtmEventsParams): Promise<any> => {
  const response = await httpClient.post('api/location/upsert-atm-events', params);
  return response.data;
};

export const useUpsertGoaEvents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertGoaEventsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-readings'] });
      queryClient.invalidateQueries({ queryKey: ['goa-events'] });
    },
  });
};

export const useUpsertTrkEvents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertTrkEventsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-readings'] });
      queryClient.invalidateQueries({ queryKey: ['trk-events'] });
    },
  });
};

export const useUpsertAtmEvents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertAtmEventsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-readings'] });
      queryClient.invalidateQueries({ queryKey: ['atm-events'] });
    },
  });
}; 

export const useGoaEvents = (locationId: string, serviceDate: string, onlyActive?: boolean, locationVisitId?: number) => {
  const queryClient = useQueryClient();

  // Fetch current day GOA events
  const currentDayEvents = useQuery({
    queryKey: ['goa-events', 'current-day', locationId, serviceDate, onlyActive, locationVisitId],
    queryFn: async () => {
      console.log("=== useGoaEvents currentDayEvents queryFn ===");
      console.log("locationId:", locationId);
      console.log("serviceDate:", serviceDate);
      console.log("onlyActive:", onlyActive);
      console.log("locationVisitId:", locationVisitId);
      const queryParams: any = {};
      if (onlyActive) queryParams.onlyActive = 'true';
      if (locationVisitId) queryParams.locationVisitId = locationVisitId.toString();
      const response = await httpClient.get(`api/location/current-day-goa-events/${locationId}/${serviceDate}`, queryParams);
      console.log("currentDayEvents response:", response.data);
      return response.data;
    },
    enabled: !!locationId && !!serviceDate,
  });

  // Fetch previous GOA events
  const previousEvents = useQuery({
    queryKey: ['goa-events', 'previous', locationId, serviceDate, onlyActive],
    queryFn: async () => {
      const queryParams = onlyActive ? { onlyActive: 'true' } : {};
      const response = await httpClient.get(`api/location/goa-previous-events/${locationId}/${serviceDate}`, queryParams);
      return response.data;
    },
    enabled: !!locationId && !!serviceDate,
  });

  // Combine the data
  const combinedData = useMemo(() => {
    // Return data as soon as either query completes, don't wait for both
    const currentEvents = currentDayEvents.data?.currentDayEvents || [];
    const previousEventsMap = previousEvents.data?.previousEvents || {};

    return {
      currentEvents,
      previousEvents: previousEventsMap,
      locationId: parseInt(locationId),
      serviceDate,
    };
  }, [currentDayEvents.data, previousEvents.data, locationId, serviceDate]);

  return {
    data: combinedData,
    isLoading: currentDayEvents.isLoading || previousEvents.isLoading,
    error: currentDayEvents.error || previousEvents.error,
    refetch: () => {
      currentDayEvents.refetch();
      previousEvents.refetch();
    },
    // Individual query states for more granular control
    currentDayEvents,
    previousEvents,
  };
};

interface BulkUpdateGoaEventsParams {
  updates: Array<{
    id: number;
    hardIn?: number;
    hardOut?: number;
  }>;
}

interface BulkUpdateGoaEventsResponse {
  updatedEvents: any[];
  errors: Array<{
    id: number;
    error: string;
  }>;
  successCount: number;
  errorCount: number;
}

export const useBulkUpdateGoaEvents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: BulkUpdateGoaEventsParams): Promise<BulkUpdateGoaEventsResponse> => {
      const response = await httpClient.post<BulkUpdateGoaEventsResponse>('api/goa/bulk-update', params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      console.log('Bulk update GOA events successful:', data);
      
      // Invalidate relevant queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['location-readings'] });
      queryClient.invalidateQueries({ queryKey: ['goa-events'] });
    },
    onError: (error) => {
      console.error('Failed to bulk update GOA events:', error);
    },
  });
}; 